/**
 * ============================================================================
 * USER / TENANT PAGE: Create Broadcast Campaign (Full-Page Workflow)
 * ============================================================================
 * Purpose:
 *   Dedicated full-page multi-step wizard for configuring, customizing, and
 *   launching WhatsApp broadcast campaigns.
 *
 * Architecture:
 *   - Persistent sticky WhatsApp Device Preview fixed on the right side across all steps.
 *   - Scrollable left side for active step configuration.
 *
 * Steps Flow:
 *   1. Choose Template (Select Approved WhatsApp Template)
 *   2. Campaign Title (Campaign Title & Name)
 *   3. Audience / Excel (Upload spreadsheet, paste numbers, or pick contacts)
 *   4. Variable Mapping (Dynamic Parameters mapped in real time to the live preview)
 *   5. Sender & Launch (Sender WhatsApp Number, Schedule, Summary & Dispatch)
 * ============================================================================
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import WhatsAppPreview from "../templates/WhatsAppPreview";
import campaignService from "../../services/campaignService";
import phoneNumberService from "../../services/phoneNumberService";
import contactService from "../../services/contactService";

// Feather / Heroicon SVG icons
const ArrowLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
  </svg>
);
const MegaphoneIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.455a20.443 20.443 0 0 1-1.378-3.957m3.04-9.442c.253-.962.584-1.892.985-2.783.247-.55.06-1.21-.463-1.511l-.657-.38c-.551-.318-1.26-.117-1.527.455a20.443 20.443 0 0 0-1.378 3.957m15.69 4.316a11.96 11.96 0 0 1-5.187 4.225m0-8.45A11.96 11.96 0 0 1 21.05 12" />
  </svg>
);
const MagnifyingGlassIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
);
const DocumentArrowUpIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m6.75 12-3-3m0 0-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>
);
const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);
const PaperAirplaneIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
  </svg>
);
const PhotoIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
  </svg>
);
const VideoCameraIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9A2.25 2.25 0 0 0 13.5 5.25h-9A2.25 2.25 0 0 0 2.25 7.5v9A2.25 2.25 0 0 0 4.5 18.75Z" />
  </svg>
);
const DocumentTextIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>
);
const ArrowUpTrayIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
  </svg>
);
const XCircleIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);
const ClockIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);
const ExclamationTriangleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
  </svg>
);
const UserGroupIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.059 2.772m0 0a5.972 5.972 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
  </svg>
);
const FolderIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
  </svg>
);
const PhoneIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
  </svg>
);
const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
);
const ArrowsRightLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
  </svg>
);
const ArrowPathIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const STEPS = [
  { id: 1, label: "Choose Template", desc: "Select Approved Template" },
  { id: 2, label: "Campaign Title", desc: "Title & Campaign Details" },
  { id: 3, label: "Audience / Excel", desc: "Contacts & Recipients" },
  { id: 4, label: "Variable Mapping", desc: "Live Preview & Parameters" },
  { id: 5, label: "Sender & Launch", desc: "Sender, Schedule & Dispatch" },
];

export default function CreateCampaign() {
  const navigate = useNavigate();
  const { id: routeEditId } = useParams();
  const [searchParams] = useSearchParams();
  const editId = routeEditId || searchParams.get("edit");
  const [isEditing, setIsEditing] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  // Wizard active step (1 to 5)
  const [currentStep, setCurrentStep] = useState(1);
  // Direction: 1 = forward (left), -1 = backward (right)
  const directionRef = useRef(1);

  // Animated step setter — always tracks direction before changing step
  const goToStep = useCallback((next) => {
    directionRef.current = next > currentStep ? 1 : -1;
    setCurrentStep(next);
  }, [currentStep]);

  // Metadata states
  const [templates, setTemplates] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  // Step 1: Choose Template
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateSearch, setTemplateSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL"); // "ALL" | "APPROVED" | "PENDING"

  // Step 2: Campaign Title
  const [campaignName, setCampaignName] = useState("");

  // Step 3: Audience & Recipients
  const [audienceType, setAudienceType] = useState("excel"); // "excel" | "manual" | "contacts"
  const [excelFile, setExcelFile] = useState(null);
  const [excelData, setExcelData] = useState(null); // { columns, totalRows, detectedPhoneColumn, sampleRows, rows }
  const [phoneColumn, setPhoneColumn] = useState("");
  const [manualNumbers, setManualNumbers] = useState("");
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("all");
  const [groupSearch, setGroupSearch] = useState("");
  const [contactSearch, setContactSearch] = useState("");

  // Step 4: Variable Mapping & Media URL / Upload
  const [variableMapping, setVariableMapping] = useState({});
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaUploadMode, setMediaUploadMode] = useState("computer"); // "computer" | "url"
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [uploadedMediaName, setUploadedMediaName] = useState("");
  const [uploadedMediaSize, setUploadedMediaSize] = useState("");
  const [mediaFile, setMediaFile] = useState(null);

  // Step 5: Sender WhatsApp Number & Schedule & Delivery Window
  const [senderSelectionMode, setSenderSelectionMode] = useState("single"); // "single" | "multiple"
  const [senderNumber, setSenderNumber] = useState("");
  const [selectedSenderNumbers, setSelectedSenderNumbers] = useState([]);
  const [senderDistributionMode, setSenderDistributionMode] = useState("SAME"); // "SAME" | "DIFFERENT"
  const [distributionSeed, setDistributionSeed] = useState(0);
  const [scheduleType, setScheduleType] = useState("now"); // "now" | "later"
  const [scheduledAt, setScheduledAt] = useState("");

  // Submitting state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load Metadata (Templates, Phone Numbers, Contacts, Groups)
  const loadMetadata = useCallback(async () => {
    setLoadingMeta(true);
    try {
      const [tplRes, phoneRes, contactRes, groupRes] = await Promise.all([
        campaignService.getUserTemplates({ all: "true" }),
        phoneNumberService.get({ limit: 100 }),
        contactService.get({ limit: 1000 }).catch(() => ({ success: false })),
        contactService.groups.get({ limit: 500 }).catch(() => ({ success: false })),
      ]);

      if (tplRes && (tplRes.success || Array.isArray(tplRes.data) || Array.isArray(tplRes.templates))) {
        setTemplates(tplRes.data || tplRes.templates || []);
      }
      if (phoneRes && phoneRes.success) {
        const pList = phoneRes.data || [];
        setPhoneNumbers(pList);
        if (pList.length > 0) {
          if (!senderNumber) setSenderNumber(pList[0].str_phone_number || "");
          setSelectedSenderNumbers((prev) =>
            prev.length > 0 ? prev : pList.map((p) => p.str_phone_number).filter(Boolean)
          );
        }
      }
      if (contactRes && (contactRes.success || Array.isArray(contactRes.data) || Array.isArray(contactRes))) {
        setContacts(Array.isArray(contactRes.data) ? contactRes.data : (Array.isArray(contactRes) ? contactRes : []));
      }
      if (groupRes && (groupRes.success || Array.isArray(groupRes.data) || Array.isArray(groupRes))) {
        const gList = Array.isArray(groupRes.data) ? groupRes.data : (Array.isArray(groupRes) ? groupRes : []);
        setGroups(gList);
      }
    } catch (err) {
      console.error("Failed to load campaign metadata:", err);
      Swal.fire({
        icon: "error",
        title: "Load Error",
        text: "Could not load templates or phone numbers. Please refresh the page.",
      });
    } finally {
      setLoadingMeta(false);
    }
  }, [senderNumber]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  // Load Draft Campaign if in Edit mode
  useEffect(() => {
    if (!editId) return;
    const fetchDraft = async () => {
      try {
        const res = await campaignService.view(editId);
        const camp = res?.data || res?.campaign || res;
        if (camp && (camp.str_campaign_name || camp.str_template_name)) {
          setIsEditing(true);
          setEditingCampaign(camp);
          setCampaignName(camp.str_campaign_name || "");
          if (camp.str_sender_number) setSenderNumber(camp.str_sender_number);
          if (camp.str_sender_selection_mode) {
            setSenderSelectionMode(camp.str_sender_selection_mode.toLowerCase());
          }
          if (Array.isArray(camp.arr_sender_numbers) && camp.arr_sender_numbers.length > 0) {
            setSelectedSenderNumbers(camp.arr_sender_numbers);
          }
          if (camp.str_sender_distribution_mode) {
            setSenderDistributionMode(camp.str_sender_distribution_mode);
          }
          if (camp.obj_variable_mapping) setVariableMapping(camp.obj_variable_mapping);
          if (camp.obj_template_data?.headerMedia?.url) {
            setMediaUrl(camp.obj_template_data.headerMedia.url);
          }
          if (camp.dttm_scheduled_at) {
            setScheduleType("later");
            setScheduledAt(camp.dttm_scheduled_at);
          }

          // Match template from loaded templates list or use draft's template data
          const tpl = templates.find(
            (t) =>
              t.name === camp.str_template_name ||
              String(t.id) === String(camp.obj_template_data?.templateId)
          );
          if (tpl) {
            setSelectedTemplate(tpl);
          } else if (camp.obj_template_data && camp.obj_template_data.name) {
            setSelectedTemplate({
              id: camp.obj_template_data.templateId || 0,
              name: camp.obj_template_data.name,
              category: camp.obj_template_data.category || camp.str_campaign_type || "MARKETING",
              headerType: camp.obj_template_data.headerType || "NONE",
              language: camp.obj_template_data.language || "en",
              variables: camp.obj_variable_mapping ? Object.keys(camp.obj_variable_mapping) : [],
            });
          }

          // Restore recipients if available
          if (camp.arr_recipients && camp.arr_recipients.length > 0) {
            const nums = camp.arr_recipients.map((r) => r.number).filter(Boolean);
            if (nums.length > 0) {
              setAudienceType("manual");
              setManualNumbers(nums.join("\n"));
            }
          }
        }
      } catch (err) {
        console.error("Failed to load draft campaign for editing:", err);
      }
    };

    fetchDraft();
  }, [editId, templates]);

  // Approval counts for Step 1
  const approvedTemplatesCount = useMemo(() => {
    return templates.filter((t) => (t.status || t.str_status || "").toUpperCase() === "APPROVED").length;
  }, [templates]);

  const pendingTemplatesCount = useMemo(() => {
    return templates.filter((t) => (t.status || t.str_status || "").toUpperCase() !== "APPROVED").length;
  }, [templates]);

  // Filter templates for Step 1
  const filteredTemplates = useMemo(() => {
    let list = templates;
    if (categoryFilter !== "ALL") {
      list = list.filter((t) => (t.category || "").toUpperCase() === categoryFilter);
    }
    if (statusFilter === "APPROVED") {
      list = list.filter((t) => (t.status || t.str_status || "").toUpperCase() === "APPROVED");
    } else if (statusFilter === "PENDING") {
      list = list.filter((t) => (t.status || t.str_status || "").toUpperCase() !== "APPROVED");
    }
    if (templateSearch.trim()) {
      const q = templateSearch.toLowerCase().trim();
      list = list.filter(
        (t) =>
          (t.name || "").toLowerCase().includes(q) ||
          (t.category || "").toLowerCase().includes(q) ||
          (t.bodyText || "").toLowerCase().includes(q) ||
          (t.status || t.str_status || "").toLowerCase().includes(q)
      );
    }
    // Sort APPROVED templates first so they appear at the top, and PENDING at the bottom
    return list.slice().sort((a, b) => {
      const aApp = (a.status || a.str_status || "").toUpperCase() === "APPROVED";
      const bApp = (b.status || b.str_status || "").toUpperCase() === "APPROVED";
      if (aApp && !bApp) return -1;
      if (!aApp && bApp) return 1;
      return 0;
    });
  }, [templates, categoryFilter, statusFilter, templateSearch]);

  // Excel Upload Handler for Step 3
  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setExcelFile(file);
    const formData = new FormData();
    formData.append("file", file);

    Swal.fire({
      title: "Analyzing Excel...",
      text: "Parsing columns and rows for audience list...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const res = await campaignService.uploadExcel(formData);
      if (res && res.success) {
        setExcelData(res);
        setPhoneColumn(res.detectedPhoneColumn || res.columns[0] || "");

        // Auto-map template variables if column headers match
        if (selectedTemplate && Array.isArray(selectedTemplate.variables)) {
          const autoMap = {};
          selectedTemplate.variables.forEach((v, idx) => {
            const matchedCol = res.columns[idx + 1] || res.columns[idx] || "";
            autoMap[v] = { type: "column", value: matchedCol };
          });
          setVariableMapping(autoMap);
        }

        Swal.fire({
          icon: "success",
          title: "File Analyzed!",
          text: `Found ${res.totalRows} audience rows across ${res.columns.length} columns.`,
          timer: 1800,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Parsing Failed",
          text: res?.error || "Could not parse Excel file.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Upload Error",
        text: err.response?.data?.error || err.message,
      });
    }
  };

  // Format bytes helper for media upload file size
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Header media computer file upload handler
  const handleMediaFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      return Swal.fire({
        icon: "error",
        title: "File Too Large",
        text: "Please select a media file under 25 MB.",
      });
    }

    setMediaFile(file);
    setUploadedMediaName(file.name);
    setUploadedMediaSize(formatBytes(file.size));

    // Instant local preview for WhatsApp preview device frame
    const localUrl = URL.createObjectURL(file);
    setMediaUrl(localUrl);

    setIsUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await campaignService.uploadMedia(formData);
      if (res && res.success && res.url) {
        setMediaUrl(res.url);
        if (res.fileName) setUploadedMediaName(res.fileName);
      }
    } catch (err) {
      console.warn("Server media upload warning:", err);
      // Retain localUrl so the preview remains active
    } finally {
      setIsUploadingMedia(false);
    }
  };

  // Compute live preview sample values based on variable mappings across all steps
  const previewSamples = useMemo(() => {
    if (!selectedTemplate || !Array.isArray(selectedTemplate.variables)) return {};
    const map = {};
    const sampleRow = excelData?.sampleRows?.[0] || {};

    selectedTemplate.variables.forEach((v) => {
      const config = variableMapping[v];
      if (config) {
        if (config.type === "column" && config.value) {
          map[v] = sampleRow[config.value] !== undefined ? String(sampleRow[config.value]) : `[${config.value}]`;
        } else if (config.type === "static" && config.value) {
          map[v] = config.value;
        } else {
          map[v] = `[${v}]`;
        }
      } else {
        map[v] = `[${v}]`;
      }
    });

    return map;
  }, [selectedTemplate, variableMapping, excelData]);

  // Check if ALL template variables have been assigned a non-empty value (used to enable/disable Step 4 Next)
  const allVariablesMapped = useMemo(() => {
    if (!selectedTemplate || !Array.isArray(selectedTemplate.variables) || selectedTemplate.variables.length === 0) {
      // No variables required — nothing to block
      return true;
    }
    return selectedTemplate.variables.every((v) => {
      const config = variableMapping[v];
      return config && config.value && String(config.value).trim() !== "";
    });
  }, [selectedTemplate, variableMapping]);

  // ==========================================
  // Contact Groups & Selection Helpers
  // ==========================================
  const contactBelongsToGroup = useCallback((c, grp) => {
    if (!grp || grp._id === "all") return true;
    const targetId = String(grp._id || grp.id || "");
    const targetName = String(grp.str_group_name || grp.groupName || "").trim().toLowerCase();

    const cGroupId = String(
      c.str_group_id ||
      c.group?._id ||
      (Array.isArray(c.arr_groups) && c.arr_groups[0]) ||
      c.group ||
      ""
    );
    const cGroupName = String(
      c.str_group_name ||
      c.group?.str_group_name ||
      c.group?.groupName ||
      ""
    ).trim().toLowerCase();

    if (targetId && cGroupId === targetId) return true;
    if (targetName && cGroupName === targetName) return true;
    if (Array.isArray(c.arr_groups)) {
      const matched = c.arr_groups.some((g) => {
        const gStr = String(g || "").trim().toLowerCase();
        return (targetId && gStr === targetId.toLowerCase()) || (targetName && gStr === targetName);
      });
      if (matched) return true;
    }
    return false;
  }, []);

  const getContactDisplayGroup = useCallback((c) => {
    if (c.str_group_name) return c.str_group_name;
    if (c.group && typeof c.group === "object") return c.group.str_group_name || c.group.groupName || "";
    const grpId = c.str_group_id || c.group || (Array.isArray(c.arr_groups) && c.arr_groups[0]);
    if (grpId) {
      const found = groups.find((g) => String(g._id || g.id) === String(grpId));
      if (found) return found.str_group_name || found.groupName || "";
    }
    return "";
  }, [groups]);

  // Combined list of groups (from API + contacts with groups)
  const allAvailableGroups = useMemo(() => {
    const list = [...groups];
    contacts.forEach((c) => {
      const gName = c.str_group_name || (c.group && typeof c.group === "object" ? c.group.str_group_name : "");
      const gId = c.str_group_id || (c.group && typeof c.group === "string" ? c.group : "");
      if (gName && !list.some((g) => (g.str_group_name || g.groupName) === gName || String(g._id || g.id) === String(gId))) {
        list.push({
          _id: gId || `grp_${gName}`,
          str_group_name: gName,
          int_total_contacts: 0,
        });
      }
    });
    return list;
  }, [groups, contacts]);

  // Groups with calculated stats
  const groupsWithStats = useMemo(() => {
    return allAvailableGroups.map((grp) => {
      const grpContacts = contacts.filter((c) => contactBelongsToGroup(c, grp));
      const totalCount = grpContacts.length;
      const selectedCount = grpContacts.filter((c) => {
        const id = c._id || c.int_contact_id || c.id;
        return selectedContactIds.includes(id);
      }).length;
      const isAllSelected = totalCount > 0 && selectedCount === totalCount;
      const isPartiallySelected = selectedCount > 0 && selectedCount < totalCount;

      return {
        ...grp,
        totalCount,
        selectedCount,
        isAllSelected,
        isPartiallySelected,
        contacts: grpContacts,
      };
    });
  }, [allAvailableGroups, contacts, selectedContactIds, contactBelongsToGroup]);

  // All contacts virtual group stats
  const allContactsStats = useMemo(() => {
    const totalCount = contacts.length;
    const selectedCount = contacts.filter((c) => {
      const id = c._id || c.int_contact_id || c.id;
      return selectedContactIds.includes(id);
    }).length;
    return {
      _id: "all",
      str_group_name: "All Contacts",
      totalCount,
      selectedCount,
      isAllSelected: totalCount > 0 && selectedCount === totalCount,
      isPartiallySelected: selectedCount > 0 && selectedCount < totalCount,
    };
  }, [contacts, selectedContactIds]);

  // Currently active group object for bottom numbers display
  const activeGroupObj = useMemo(() => {
    if (selectedGroupId === "all") return allContactsStats;
    const found = groupsWithStats.find((g) => String(g._id || g.id) === String(selectedGroupId));
    return found || allContactsStats;
  }, [selectedGroupId, groupsWithStats, allContactsStats]);

  // Contacts belonging to the currently active group
  const activeGroupContacts = useMemo(() => {
    if (selectedGroupId === "all") return contacts;
    return contacts.filter((c) => contactBelongsToGroup(c, activeGroupObj));
  }, [selectedGroupId, contacts, activeGroupObj, contactBelongsToGroup]);

  // Filtered contacts in the active group (by search query)
  const filteredActiveContacts = useMemo(() => {
    if (!contactSearch.trim()) return activeGroupContacts;
    const q = contactSearch.toLowerCase().trim();
    return activeGroupContacts.filter((c) => {
      const name = String(c.str_name || c.name || c.str_first_name || "").toLowerCase();
      const phone = String(c.str_phone_number || c.number || c.str_mobile_number || "").toLowerCase();
      const grp = String(getContactDisplayGroup(c) || "").toLowerCase();
      return name.includes(q) || phone.includes(q) || grp.includes(q);
    });
  }, [activeGroupContacts, contactSearch, getContactDisplayGroup]);

  // Group cards filtered by group search
  const filteredGroupCards = useMemo(() => {
    if (!groupSearch.trim()) return groupsWithStats;
    const q = groupSearch.toLowerCase().trim();
    return groupsWithStats.filter((g) => (g.str_group_name || g.groupName || "").toLowerCase().includes(q));
  }, [groupsWithStats, groupSearch]);

  // Selection Action Handlers
  const handleToggleGroup = useCallback((grp, e) => {
    if (e) e.stopPropagation();
    const grpContacts = grp._id === "all" ? contacts : contacts.filter((c) => contactBelongsToGroup(c, grp));
    const grpContactIds = grpContacts.map((c) => c._id || c.int_contact_id || c.id);
    if (grpContactIds.length === 0) return;

    const allSelected = grpContactIds.every((id) => selectedContactIds.includes(id));
    if (allSelected) {
      setSelectedContactIds((prev) => prev.filter((id) => !grpContactIds.includes(id)));
    } else {
      setSelectedContactIds((prev) => Array.from(new Set([...prev, ...grpContactIds])));
    }
  }, [contacts, contactBelongsToGroup, selectedContactIds]);

  const handleToggleAllActiveContacts = useCallback(() => {
    const targetList = filteredActiveContacts.length > 0 ? filteredActiveContacts : activeGroupContacts;
    const targetIds = targetList.map((c) => c._id || c.int_contact_id || c.id);
    if (targetIds.length === 0) return;

    const allSelected = targetIds.every((id) => selectedContactIds.includes(id));
    if (allSelected) {
      setSelectedContactIds((prev) => prev.filter((id) => !targetIds.includes(id)));
    } else {
      setSelectedContactIds((prev) => Array.from(new Set([...prev, ...targetIds])));
    }
  }, [filteredActiveContacts, activeGroupContacts, selectedContactIds]);

  const handleToggleContact = useCallback((id) => {
    setSelectedContactIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleToggleAllContacts = useCallback(() => {
    if (selectedContactIds.length === contacts.length) {
      setSelectedContactIds([]);
    } else {
      setSelectedContactIds(contacts.map((c) => c._id || c.int_contact_id || c.id));
    }
  }, [contacts, selectedContactIds]);

  // Recipient count calculation for Step 3 & 5
  const totalRecipientsCount = useMemo(() => {
    if (audienceType === "excel") {
      return excelData?.totalRows || 0;
    }
    if (audienceType === "manual") {
      return manualNumbers.split(/[\n,;]+/).map((s) => s.trim()).filter((s) => s.length >= 10).length;
    }
    if (audienceType === "contacts") {
      return selectedContactIds.length;
    }
    return 0;
  }, [audienceType, excelData, manualNumbers, selectedContactIds]);

  // Array of cleaned recipient numbers
  const recipientNumbersList = useMemo(() => {
    if (audienceType === "excel" && excelData && phoneColumn) {
      return (excelData.rows || [])
        .map((r) => String(r[phoneColumn] || "").replace(/[^0-9]/g, ""))
        .filter((s) => s.length >= 10);
    }
    if (audienceType === "manual") {
      return manualNumbers
        .split(/[\n,;]+/)
        .map((s) => s.replace(/[^0-9]/g, "").trim())
        .filter((s) => s.length >= 10);
    }
    if (audienceType === "contacts") {
      return contacts
        .filter((c) => selectedContactIds.includes(c._id || c.int_contact_id || c.id))
        .map((c) => String(c.str_phone_number || c.number || c.str_mobile_number || "").replace(/[^0-9]/g, ""))
        .filter((s) => s.length >= 10);
    }
    return [];
  }, [audienceType, excelData, phoneColumn, manualNumbers, contacts, selectedContactIds]);

  // Live sender distribution preview calculation across selected sender numbers
  const liveDistributionPreview = useMemo(() => {
    const activeNumbers = senderSelectionMode === "multiple"
      ? selectedSenderNumbers.filter(Boolean)
      : [senderNumber || (phoneNumbers[0]?.str_phone_number || "")].filter(Boolean);

    if (activeNumbers.length === 0) return [];
    const N = totalRecipientsCount;
    const K = activeNumbers.length;

    if (N <= 0) {
      return activeNumbers.map((num) => ({ number: num, count: 0, percent: "0.0%" }));
    }

    if (K === 1 || senderSelectionMode === "single") {
      return [{ number: activeNumbers[0], count: N, percent: "100.0%" }];
    }

    if (senderDistributionMode === "SAME") {
      const base = Math.floor(N / K);
      const rem = N % K;
      return activeNumbers.map((num, i) => {
        const count = base + (i < rem ? 1 : 0);
        return {
          number: num,
          count,
          percent: N > 0 ? ((count / N) * 100).toFixed(1) + "%" : "0.0%",
        };
      });
    }

    // DIFFERENT DISTRIBUTION: Controlled integer partition
    let s = (distributionSeed * 9301 + 49297 + N * 13 + K * 7) % 233280;
    const rnd = () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };

    const weights = [];
    for (let i = 0; i < K; i++) {
      weights.push(0.4 + rnd() * 1.2);
    }
    const sumWeights = weights.reduce((a, b) => a + b, 0);

    const counts = weights.map((w) => Math.max(1, Math.floor((w / sumWeights) * N)));
    let allocated = counts.reduce((a, b) => a + b, 0);

    while (allocated < N) {
      const pick = Math.floor(rnd() * K);
      counts[pick] += 1;
      allocated += 1;
    }
    while (allocated > N) {
      const pick = Math.floor(rnd() * K);
      if (counts[pick] > 1) {
        counts[pick] -= 1;
        allocated -= 1;
      }
    }

    return activeNumbers.map((num, i) => ({
      number: num,
      count: counts[i],
      percent: N > 0 ? ((counts[i] / N) * 100).toFixed(1) + "%" : "0.0%",
    }));
  }, [
    totalRecipientsCount,
    senderSelectionMode,
    selectedSenderNumbers,
    senderNumber,
    phoneNumbers,
    senderDistributionMode,
    distributionSeed,
  ]);

  // Cost calculation & wallet balance states
  const [costData, setCostData] = useState(null);
  const [loadingCost, setLoadingCost] = useState(false);

  // Fetch country rate calculation & wallet check
  const fetchCostEstimate = useCallback(async () => {
    if (!selectedTemplate || recipientNumbersList.length === 0) {
      setCostData(null);
      return;
    }
    setLoadingCost(true);
    try {
      const res = await campaignService.calculateCost({
        category: selectedTemplate.category || "MARKETING",
        templateName: selectedTemplate.name,
        recipients: recipientNumbersList,
      });
      if (res && (res.success || res.totalEstimatedCost !== undefined)) {
        setCostData(res.data || res);
      }
    } catch (err) {
      console.warn("Could not calculate campaign cost:", err);
    } finally {
      setLoadingCost(false);
    }
  }, [selectedTemplate, recipientNumbersList]);

  useEffect(() => {
    if (currentStep === 5 || (selectedTemplate && recipientNumbersList.length > 0)) {
      fetchCostEstimate();
    }
  }, [currentStep, selectedTemplate, recipientNumbersList, fetchCostEstimate]);

  // Step Validation & Forward Navigation
  const handleNextStep = (step) => {
    directionRef.current = 1;
    if (step === 1) {
      if (!selectedTemplate) {
        return Swal.fire({
          icon: "warning",
          title: "Template Required",
          text: "Please select an approved WhatsApp template to proceed.",
          confirmButtonColor: "#2563eb",
        });
      }
      if ((selectedTemplate.status || selectedTemplate.str_status || "").toUpperCase() !== "APPROVED") {
        return Swal.fire({
          icon: "warning",
          title: "Template Not Approved",
          text: `Template "${selectedTemplate.name}" is pending approval from Meta and cannot be dispatched. Please select an approved template.`,
          confirmButtonColor: "#2563eb",
        });
      }
      setCurrentStep(2);
    } else if (step === 2) {
      if (!campaignName.trim()) {
        return Swal.fire({
          icon: "warning",
          title: "Campaign Title Required",
          text: "Please enter a campaign title before proceeding.",
          confirmButtonColor: "#2563eb",
        });
      }
      setCurrentStep(3);
    } else if (step === 3) {
      if (totalRecipientsCount === 0) {
        return Swal.fire({
          icon: "warning",
          title: "Audience Required",
          text: "Please upload an Excel file, paste valid phone numbers, or select saved contacts.",
          confirmButtonColor: "#2563eb",
        });
      }
      if (audienceType === "excel" && !phoneColumn) {
        return Swal.fire({
          icon: "warning",
          title: "Phone Column Required",
          text: "Please select which Excel column contains recipient phone numbers.",
          confirmButtonColor: "#2563eb",
        });
      }
      setCurrentStep(4);
    } else if (step === 4) {
      if (
        selectedTemplate?.headerType &&
        ["IMAGE", "VIDEO", "DOCUMENT"].includes(selectedTemplate.headerType) &&
        !mediaUrl
      ) {
        return Swal.fire({
          icon: "warning",
          title: "Header Media Required",
          text: `Please upload or provide a ${selectedTemplate.headerType.toLowerCase()} file for this template's header.`,
          confirmButtonColor: "#2563eb",
        });
      }
      setCurrentStep(5);
    }
  };

  // Submit / Launch Campaign (Invoked from Step 5)
  const handleSaveOrSend = async (sendNow = false) => {
    if (!selectedTemplate) {
      return Swal.fire({ icon: "warning", title: "Template Required", text: "Please select a template." });
    }
    if (!campaignName.trim()) {
      return Swal.fire({ icon: "warning", title: "Title Required", text: "Please enter a campaign title." });
    }
    if (senderSelectionMode === "multiple") {
      if (!selectedSenderNumbers || selectedSenderNumbers.length === 0) {
        return Swal.fire({
          icon: "warning",
          title: "Sender Numbers Required",
          text: "Please select at least one WhatsApp sender number for multi-number distribution.",
          confirmButtonColor: "#2563eb",
        });
      }
    } else {
      if (!senderNumber && phoneNumbers.length === 0) {
        return Swal.fire({
          icon: "warning",
          title: "Sender Number Required",
          text: "Please select an authorized WhatsApp sender number.",
          confirmButtonColor: "#2563eb",
        });
      }
    }

    // Build recipients array
    let recipients = [];
    if (audienceType === "excel" && excelData) {
      recipients = (excelData.rows || []).map((row) => {
        const rawPhone = row[phoneColumn];
        const vars = {};
        if (selectedTemplate.variables) {
          selectedTemplate.variables.forEach((v) => {
            const mapped = variableMapping[v];
            if (mapped?.type === "column" && mapped.value) {
              const cleanKey = v.replace(/[{}]/g, "");
              vars[cleanKey] = row[mapped.value] !== undefined ? String(row[mapped.value]) : "";
            } else if (mapped?.type === "static") {
              const cleanKey = v.replace(/[{}]/g, "");
              vars[cleanKey] = mapped.value || "";
            }
          });
        }
        return { number: String(rawPhone || ""), variables: vars };
      });
    } else if (audienceType === "manual") {
      const numbers = manualNumbers.split(/[\n,;]+/).map((s) => s.trim()).filter((s) => s.length >= 10);
      const staticVars = {};
      if (selectedTemplate.variables) {
        selectedTemplate.variables.forEach((v) => {
          const cleanKey = v.replace(/[{}]/g, "");
          staticVars[cleanKey] = variableMapping[v]?.value || "";
        });
      }
      recipients = numbers.map((num) => ({ number: num, variables: staticVars }));
    } else if (audienceType === "contacts") {
      const chosen = contacts.filter((c) => selectedContactIds.includes(c._id || c.int_contact_id || c.id));
      recipients = chosen.map((c) => {
        const phone = String(c.str_phone_number || c.number || c.str_mobile_number || "").replace(/[^0-9]/g, "");
        const name = c.str_name || c.name || c.str_first_name || "";
        const vars = {};
        if (selectedTemplate.variables) {
          selectedTemplate.variables.forEach((v) => {
            const cleanKey = v.replace(/[{}]/g, "");
            vars[cleanKey] = variableMapping[v]?.value || name || "";
          });
        }
        return { number: phone, name: name, variables: vars };
      });
    }

    if (sendNow && recipients.length === 0) {
      return Swal.fire({ icon: "warning", title: "No Recipients", text: "No valid audience recipients found." });
    }

    const effectiveSender = senderNumber || (phoneNumbers.length > 0 ? phoneNumbers[0].str_phone_number : "");
    const effectiveSenders = senderSelectionMode === "multiple"
      ? (selectedSenderNumbers.length > 0 ? selectedSenderNumbers : [effectiveSender])
      : [effectiveSender];

    // Confirmation dialog before immediate broadcast
    if (sendNow) {
      if (costData && costData.hasSufficientBalance === false) {
        return Swal.fire({
          icon: "error",
          title: "Insufficient Wallet Balance",
          html: `Total required cost is <strong>₹${Number(costData.totalEstimatedCost).toFixed(2)}</strong>, but your available wallet credit is <strong>₹${Number(costData.currentBalance).toFixed(2)}</strong>.<br/><br/>Please recharge your wallet before launching this broadcast.`,
          confirmButtonColor: "#2563eb",
        });
      }

      const confirm = await Swal.fire({
        title: "Launch WhatsApp Broadcast?",
        html: `
          <div style="text-align: left; font-size: 13px; line-height: 1.6;">
            <p>You are about to broadcast <strong>"${selectedTemplate.name}"</strong> (<code>${(selectedTemplate.category || 'MARKETING').toUpperCase()}</code>) to <strong>${recipients.length} recipients</strong> via ${
              senderSelectionMode === "multiple"
                ? `<strong>${effectiveSenders.length} Sender Numbers</strong> (<code>${senderDistributionMode === 'DIFFERENT' ? 'Different Distribution' : 'Same Distribution'}</code>)`
                : `<code>${effectiveSender}</code>`
            }.</p>
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-top: 12px;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
                <span>Total Estimated Cost:</span>
                <strong style="color: #2563eb;">₹${costData?.totalEstimatedCost != null ? Number(costData.totalEstimatedCost).toFixed(2) : '0.00'}</strong>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
                <span>Current Wallet Credit:</span>
                <strong style="color: #16a34a;">₹${costData?.currentBalance != null ? Number(costData.currentBalance).toFixed(2) : '0.00'}</strong>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span>Balance After Dispatch:</span>
                <strong style="color: ${costData?.hasSufficientBalance ? '#1e293b' : '#dc2626'};">₹${costData?.balanceAfterDeduction != null ? Number(costData.balanceAfterDeduction).toFixed(2) : '0.00'}</strong>
              </div>
            </div>
            <p style="font-size: 11px; color: #64748b; margin-top: 8px;">Deductions will be cut from your wallet according to your account's country deduct rates upon dispatch.</p>
          </div>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#2563eb",
        confirmButtonText: "Yes, Launch Now 🚀",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;
    }

    setIsSubmitting(true);
    Swal.fire({
      title: sendNow ? "Launching Broadcast..." : "Saving Campaign...",
      text: "Queuing audience messages and saving campaign configuration...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const selectedPhoneObj = phoneNumbers.find((p) => p.str_phone_number === effectiveSender);

      let savedBatchSettings = {};
      try {
        const stored = localStorage.getItem("campaign_batch_settings");
        if (stored) savedBatchSettings = JSON.parse(stored);
      } catch (e) {}

      const payload = {
        str_campaign_name: campaignName.trim(),
        str_template_name: selectedTemplate.name,
        str_sender_number: effectiveSender,
        str_sender_selection_mode: senderSelectionMode === "multiple" ? "multiple" : "single",
        arr_sender_numbers: effectiveSenders,
        str_sender_distribution_mode: senderDistributionMode,
        obj_batch_settings: savedBatchSettings,
        str_waba_id: selectedPhoneObj?.str_waba_id || selectedTemplate.waba_id || "",
        str_operator: selectedPhoneObj?.str_operator || "karix",
        str_campaign_type: selectedTemplate.category || "MARKETING",
        send_now: sendNow,
        dttm_scheduled_at: scheduleType === "later" && scheduledAt ? scheduledAt : null,
        obj_template_data: {
          templateId: selectedTemplate.id,
          name: selectedTemplate.name,
          language: selectedTemplate.language || "en",
          category: selectedTemplate.category,
          headerType: selectedTemplate.headerType,
          headerMedia: mediaUrl
            ? {
              url: mediaUrl,
              fileName: uploadedMediaName || (mediaUrl ? mediaUrl.split("/").pop().split("?")[0] : ""),
              type: (selectedTemplate.headerType || "IMAGE").toLowerCase(),
            }
            : null,
        },
        obj_variable_mapping: variableMapping,
        recipients,
      };

      let res;
      if (editId) {
        res = await campaignService.edit(editId, payload);
      } else {
        res = await campaignService.create(payload);
      }

      if (res && (res.success || res.campaign)) {
        await Swal.fire({
          icon: "success",
          title: sendNow
            ? "Broadcast Launched! 🚀"
            : isEditing
              ? "Draft Updated! ✅"
              : "Campaign Saved!",
          text:
            res.message ||
            (sendNow
              ? "Messages are being dispatched to recipients."
              : isEditing
                ? "Draft campaign has been updated."
                : "Campaign has been saved successfully."),
          timer: 2000,
          showConfirmButton: false,
        });
        navigate("/workspace/campaigns");
      } else {
        Swal.fire({
          icon: "error",
          title: "Submission Error",
          text: res?.error || "Failed to save campaign.",
        });
      }
    } catch (err) {
      console.error("Create campaign error:", err);
      Swal.fire({
        icon: "error",
        title: "Execution Error",
        text: err.response?.data?.error || err.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto space-y-6">
      {/* ── Top Header & Breadcrumb ── */}
      {/* <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-200 dark:border-gray-700/80 pb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/workspace/campaigns")}
            className="p-2 bg-blue-600 text-white rounded-xl shadow-md shadow-blue-500/20 hover:bg-blue-700 transition-colors cursor-pointer flex-shrink-0"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white flex items-center gap-2.5">
            <span>{isEditing ? "Edit Draft Campaign" : "Create Broadcast Campaign"}</span>
            {isEditing && (
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300 font-mono font-bold">
                #{editId}
              </span>
            )}
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/workspace/campaigns")}
            className="px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSubmitting || !selectedTemplate || !campaignName.trim()}
            onClick={() => handleSaveOrSend(false)}
            className="px-4 py-2 text-xs font-semibold bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 text-gray-800 dark:text-gray-200 rounded-xl transition-colors cursor-pointer"
          >
            {isEditing ? "Update Draft" : "Save as Draft"}
          </button>
        </div>
      </div> */}

      {/* ── Step Indicator Bar ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center w-full">
          {STEPS.map((s, idx) => {
            const isActive = currentStep === s.id;
            const isDone = currentStep > s.id;
            return (
              <React.Fragment key={s.id}>
                {/* ── Step node ── */}
                <button
                  type="button"
                  onClick={() => {
                    if (s.id < currentStep) {
                      directionRef.current = -1;
                      setCurrentStep(s.id);
                    }
                  }}
                  style={{ transition: "all 280ms cubic-bezier(0.4,0,0.2,1)", flex: "0 0 auto" }}
                  className={`flex flex-col items-center gap-1 cursor-pointer group ${isDone ? "cursor-pointer" : isActive ? "cursor-default" : "cursor-not-allowed"}`}
                  disabled={!isDone && !isActive}
                >
                  {/* Circle */}
                  <span
                    style={{ transition: "background 280ms cubic-bezier(0.4,0,0.2,1), box-shadow 280ms ease, transform 280ms cubic-bezier(0.4,0,0.2,1)" }}
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isActive
                        ? "bg-blue-600 text-white shadow-md shadow-blue-500/30 scale-110"
                        : isDone
                          ? "bg-emerald-500 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
                      }`}
                  >
                    {isDone ? (
                      <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5" style={{ animation: "cc-check-pop 240ms cubic-bezier(0.34,1.56,0.64,1) both" }}>
                        <path d="M3 8.5l3.5 3.5 6.5-7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : s.id}
                  </span>
                  {/* Label */}
                  <span
                    style={{ transition: "color 280ms ease" }}
                    className={`text-[10px] sm:text-[11px] font-semibold whitespace-nowrap hidden sm:block ${isActive
                        ? "text-blue-600 dark:text-blue-400"
                        : isDone
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                  >
                    {s.label}
                  </span>
                </button>

                {/* ── Connector line (equal flex-1 between each step) ── */}
                {idx < STEPS.length - 1 && (
                  <div className="flex-1 mx-2 relative" style={{ height: 3, minWidth: 24 }}>
                    {/* Track */}
                    <div className="absolute inset-0 rounded-full bg-gray-200 dark:bg-gray-700" />
                    {/* Fill */}
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: 9999,
                        backgroundColor: currentStep > s.id ? "#10b981" : "transparent",
                        transform: `scaleX(${currentStep > s.id ? 1 : 0})`,
                        transformOrigin: "left center",
                        transition: "transform 400ms cubic-bezier(0.4,0,0.2,1), background-color 280ms ease",
                      }}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>


      {/* Inject checkmark pop keyframe once */}
      <style>{`
        @keyframes cc-check-pop {
          0% { transform: scale(0) rotate(-15deg); opacity: 0; }
          70% { transform: scale(1.25) rotate(3deg); opacity: 1; }
          100% { transform: scale(1) rotate(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .cc-step-motion { transition: none !important; animation: none !important; }
        }
      `}</style>

      {/* ── TWO-COLUMN WORKFLOW LAYOUT: LEFT FORM (SCROLLABLE), RIGHT PREVIEW (FIXED/STICKY) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* ── LEFT COLUMN: ACTIVE STEP CONTENT — wrapped in AnimatePresence for slide+fade ── */}
        <div className="lg:col-span-7 xl:col-span-8" style={{ minHeight: 0 }}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: directionRef.current * 32 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: directionRef.current * -24 }}
              transition={{ duration: 0.36, ease: [0.4, 0, 0.2, 1] }}
              style={{ willChange: "opacity, transform" }}
              className="space-y-6"
            >
              {/* ── STEP 1: Choose Template ── */}
              {currentStep === 1 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-7 border border-gray-200 dark:border-gray-700 shadow-xs space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-base font-bold text-gray-900 dark:text-white">
                          1. Select an Approved WhatsApp Template
                        </h2>
                        <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300/40">
                          {approvedTemplatesCount} Approved Ready
                        </span>
                        {pendingTemplatesCount > 0 && (
                          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300/40">
                            {pendingTemplatesCount} Pending Review
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Only approved templates can be used for broadcast campaigns. Pending templates are awaiting Meta approval and cannot be selected.
                      </p>
                    </div>
                  </div>

                  {/* Search and Category Filter */}
                  <div className="flex flex-wrap items-center justify-between gap-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Category Filter */}
                      <div className="flex bg-gray-100 dark:bg-gray-900 p-1 rounded-xl">
                        {["ALL", "MARKETING", "UTILITY", "AUTHENTICATION"].map((cat) => (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => setCategoryFilter(cat)}
                            className={`px-2.5 py-1 rounded-lg text-[10px] sm:text-[11px] font-semibold transition-all cursor-pointer ${categoryFilter === cat
                                ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                                : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
                              }`}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>

                      {/* Approval Status Filter */}
                      <div className="flex bg-gray-100 dark:bg-gray-900 p-1 rounded-xl">
                        {[
                          { id: "ALL", label: `All (${templates.length})` },
                          { id: "APPROVED", label: `Approved (${approvedTemplatesCount})` },
                          { id: "PENDING", label: `Pending (${pendingTemplatesCount})` },
                        ].map((st) => (
                          <button
                            key={st.id}
                            type="button"
                            onClick={() => setStatusFilter(st.id)}
                            className={`px-2.5 py-1 rounded-lg text-[10px] sm:text-[11px] font-semibold transition-all cursor-pointer ${statusFilter === st.id
                                ? "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-xs"
                                : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
                              }`}
                          >
                            {st.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="relative flex-1 min-w-[200px]">
                      <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search templates by name or content..."
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white outline-none"
                      />
                    </div>
                  </div>

                  {/* Templates Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 max-h-[340px] overflow-y-auto pr-1">
                    {filteredTemplates.map((tpl) => {
                      const isApproved = (tpl.status || tpl.str_status || "").toUpperCase() === "APPROVED";
                      const isPending = (tpl.status || tpl.str_status || "").toUpperCase() === "PENDING";
                      const isSelected = isApproved && selectedTemplate?.name === tpl.name;

                      return (
                        <div
                          key={tpl.id || tpl.name}
                          onClick={() => {
                            if (!isApproved) {
                              Swal.fire({
                                toast: true,
                                position: "top-end",
                                icon: "warning",
                                title: isPending ? "Template Pending Meta Approval" : "Template Not Approved",
                                text: isPending
                                  ? `Template "${tpl.name}" is pending Meta review and cannot be used for campaigns.`
                                  : `Template "${tpl.name}" is ${tpl.status || "not approved"} and cannot be selected.`,
                                showConfirmButton: false,
                                timer: 3500,
                              });
                              return;
                            }
                            setSelectedTemplate(tpl);
                          }}
                          className={`p-3.5 rounded-2xl border transition-all flex flex-col justify-between relative ${
                            !isApproved
                              ? "border-dashed border-amber-300 dark:border-amber-700/60 bg-amber-50/20 dark:bg-amber-950/15 opacity-75 cursor-not-allowed select-none"
                              : isSelected
                              ? "border-blue-600 bg-blue-50/70 dark:bg-blue-900/30 ring-2 ring-blue-500/30 shadow-sm cursor-pointer"
                              : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-xs cursor-pointer"
                          }`}
                        >
                          {isSelected && (
                            <div className="absolute top-3 right-3 text-blue-600 dark:text-blue-400">
                              <CheckCircleIcon className="w-5 h-5" />
                            </div>
                          )}

                          <div>
                            <div className="flex items-center justify-between gap-2 pr-6 mb-2">
                              <span className={`font-mono text-xs font-bold truncate ${
                                !isApproved ? "text-gray-600 dark:text-gray-400" : "text-gray-900 dark:text-white"
                              }`}>
                                {tpl.name}
                              </span>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                  {tpl.category}
                                </span>
                                {isApproved ? (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 border border-emerald-300/40 flex items-center gap-1">
                                    <CheckCircleIcon className="w-3 h-3 text-emerald-600" />
                                    <span>Approved</span>
                                  </span>
                                ) : isPending ? (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-100 text-amber-800 dark:bg-amber-950/70 dark:text-amber-300 border border-amber-300/60 flex items-center gap-1 shadow-xs">
                                    <ClockIcon className="w-3 h-3 text-amber-600 animate-pulse" />
                                    <span>Pending</span>
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-rose-100 text-rose-800 dark:bg-rose-950/70 dark:text-rose-300 border border-rose-300/60 flex items-center gap-1">
                                    <XCircleIcon className="w-3 h-3 text-rose-600" />
                                    <span>Rejected</span>
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-3 leading-relaxed">
                              {tpl.bodyText || "No preview body text"}
                            </p>
                          </div>

                          <div className={`flex items-center justify-between pt-2.5 mt-2.5 text-[10px] border-t ${
                            !isApproved
                              ? "border-amber-200/60 dark:border-amber-900/40 text-amber-700 dark:text-amber-400"
                              : "border-gray-100 dark:border-gray-700/60 text-gray-400"
                          }`}>
                            <span>Lang: <strong>{tpl.language}</strong></span>
                            {!isApproved ? (
                              <span className="font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                <span>Pending Meta Review (Cannot Select)</span>
                              </span>
                            ) : tpl.variableCount > 0 ? (
                              <span className="font-semibold text-blue-600 dark:text-blue-400">
                                {tpl.variableCount} Variable(s)
                              </span>
                            ) : (
                              <span>Static</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {filteredTemplates.length === 0 && (
                    <div className="text-center py-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl">
                      <p className="text-xs text-gray-500">
                        {statusFilter === "APPROVED"
                          ? "No approved templates match your search. Approved templates will appear here once approved by Meta."
                          : "No templates match your search criteria."}
                      </p>
                    </div>
                  )}

                  <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-gray-700/60">
                    <button
                      type="button"
                      disabled={!selectedTemplate || (selectedTemplate.status || selectedTemplate.str_status || "").toUpperCase() !== "APPROVED"}
                      onClick={() => handleNextStep(1)}
                      className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-xl text-xs transition-colors cursor-pointer shadow-sm"
                    >
                      Next: Campaign Title →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 2: Campaign Title (2nd Step) ── */}
              {currentStep === 2 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 border border-gray-200 dark:border-gray-700 shadow-xs space-y-6">
                  <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white">
                      2. Campaign Title & Information
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Give your campaign an identifiable title and review the selected WhatsApp template.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <label className="block text-xs font-bold text-gray-700 dark:text-gray-300">
                        Campaign Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Diwali Flash Sale Announcement"
                        value={campaignName}
                        onChange={(e) => setCampaignName(e.target.value)}
                        autoFocus
                        className="w-full px-4 py-2.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white outline-none"
                      />
                      <p className="text-[11px] text-gray-400">
                        Internal reference title visible on your dashboard logs and reports.
                      </p>
                    </div>

                    {/* Selected Template Recap Box */}
                    {selectedTemplate && (
                      <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-xs text-gray-900 dark:text-white font-mono">
                              {selectedTemplate.name}
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                              {selectedTemplate.category}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => { directionRef.current = -1; setCurrentStep(1); }}
                            className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                          >
                            ← Change Template
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
                          {selectedTemplate.bodyText}
                        </p>
                        <div className="text-[10px] text-gray-400 pt-1 flex items-center gap-4">
                          <span>Language: <strong>{selectedTemplate.language}</strong></span>
                          <span>Variables: <strong>{selectedTemplate.variableCount} dynamic variable(s)</strong></span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between pt-4 border-t border-gray-100 dark:border-gray-700/60">
                    <button
                      type="button"
                      onClick={() => { directionRef.current = -1; setCurrentStep(1); }}
                      className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-xs cursor-pointer font-semibold"
                    >
                      ← Back to Template
                    </button>
                    <button
                      type="button"
                      onClick={() => handleNextStep(2)}
                      className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-xs transition-colors cursor-pointer shadow-sm"
                    >
                      Next: Audience / Excel →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 3: Audience & Recipients (3rd Step) ── */}
              {currentStep === 3 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 border border-gray-200 dark:border-gray-700 shadow-xs space-y-6">
                  <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white">
                      3. Configure Audience & Recipients
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Upload an Excel/CSV file with contact columns, paste phone numbers manually, or select from your saved contacts.
                    </p>
                  </div>

                  {/* Audience Selection Tabs */}
                  <div className="flex border-b border-gray-200 dark:border-gray-700 gap-4">
                    <button
                      type="button"
                      onClick={() => setAudienceType("excel")}
                      className={`pb-2.5 text-xs font-bold cursor-pointer border-b-2 transition-all ${audienceType === "excel"
                          ? "border-blue-600 text-blue-600 dark:text-blue-400"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                    >
                      📊 Upload Excel / CSV
                    </button>
                    <button
                      type="button"
                      onClick={() => setAudienceType("manual")}
                      className={`pb-2.5 text-xs font-bold cursor-pointer border-b-2 transition-all ${audienceType === "manual"
                          ? "border-blue-600 text-blue-600 dark:text-blue-400"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                    >
                      ✍️ Manual Numbers
                    </button>
                    <button
                      type="button"
                      onClick={() => setAudienceType("contacts")}
                      className={`pb-2.5 text-xs font-bold cursor-pointer border-b-2 transition-all ${audienceType === "contacts"
                          ? "border-blue-600 text-blue-600 dark:text-blue-400"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                    >
                      👥 Saved Contacts & Groups ({contacts.length})
                    </button>
                  </div>

                  {/* Tab 1: Excel / CSV Upload */}
                  {audienceType === "excel" && (
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-2xl p-7 text-center bg-gray-50/50 dark:bg-gray-900/30">
                        <DocumentArrowUpIcon className="w-9 h-9 mx-auto text-blue-600 dark:text-blue-400 mb-2.5" />
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                          Upload Audience Spreadsheet (.xlsx, .xls, .csv)
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
                          Drag and drop or browse your file. Make sure it contains phone numbers and any dynamic parameters (e.g. Name, Balance).
                        </p>
                        <label className="inline-block mt-4 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-xs cursor-pointer transition-colors shadow-sm">
                          Browse File
                          <input
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            className="hidden"
                            onChange={handleExcelUpload}
                          />
                        </label>
                      </div>

                      {excelData && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 space-y-4">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                            <div>
                              <span className="text-gray-500">File: </span>
                              <strong className="font-mono text-gray-900 dark:text-white">{excelFile?.name}</strong>
                            </div>
                            <span className="px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 font-bold self-start">
                              ✓ {excelData.totalRows} Recipients Found
                            </span>
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                              Select Recipient Phone Column <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={phoneColumn}
                              onChange={(e) => setPhoneColumn(e.target.value)}
                              className="w-full px-3.5 py-2 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl font-mono cursor-pointer outline-none"
                            >
                              {excelData.columns.map((c) => (
                                <option key={c} value={c}>
                                  Column: {c} {c === excelData.detectedPhoneColumn ? " (Auto-Detected)" : ""}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Sample rows preview table */}
                          {excelData.sampleRows && excelData.sampleRows.length > 0 && (
                            <div className="space-y-1.5">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                                First {excelData.sampleRows.length} Rows Preview
                              </span>
                              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 max-h-48">
                                <table className="w-full text-[11px] text-left">
                                  <thead className="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 sticky top-0">
                                    <tr>
                                      {excelData.columns.map((c) => (
                                        <th key={c} className="px-3 py-2 font-bold font-mono">
                                          {c}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {excelData.sampleRows.map((row, idx) => (
                                      <tr key={idx} className="bg-white dark:bg-gray-900/60">
                                        {excelData.columns.map((col) => (
                                          <td key={col} className="px-3 py-1.5 font-mono text-gray-700 dark:text-gray-300 truncate max-w-xs">
                                            {row[col] !== undefined ? String(row[col]) : ""}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tab 2: Manual Numbers */}
                  {audienceType === "manual" && (
                    <div className="space-y-2">
                      <label className="block text-xs font-bold text-gray-700 dark:text-gray-300">
                        Paste Phone Numbers (one per line or comma-separated)
                      </label>
                      <textarea
                        rows={8}
                        placeholder="919876543210&#10;919876543211&#10;919876543212"
                        value={manualNumbers}
                        onChange={(e) => setManualNumbers(e.target.value)}
                        className="w-full p-4 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl font-mono text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex items-center justify-between text-[11px] text-gray-400">
                        <span>Always include country code (e.g. 91 for India).</span>
                        <span className="font-bold text-blue-600 dark:text-blue-400">
                          {totalRecipientsCount} valid numbers detected
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Tab 3: Saved Contacts & Contact Groups */}
                  {audienceType === "contacts" && (
                    <div className="space-y-5">
                      {/* Global Overview Bar */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 bg-gradient-to-r from-blue-50/70 via-indigo-50/40 to-blue-50/70 dark:from-blue-950/30 dark:via-indigo-950/20 dark:to-blue-950/30 border border-blue-200/80 dark:border-blue-800/60 rounded-2xl">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-xs shrink-0">
                            <UserGroupIcon className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-gray-900 dark:text-white">
                              Audience Contacts & Groups
                            </div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400">
                              <span className="font-bold text-blue-600 dark:text-blue-400">
                                {selectedContactIds.length}
                              </span>{" "}
                              of {contacts.length} numbers selected for this broadcast
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleToggleAllContacts}
                            className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer shadow-xs ${
                              selectedContactIds.length === contacts.length && contacts.length > 0
                                ? "bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200"
                                : "bg-blue-600 hover:bg-blue-700 text-white"
                            }`}
                          >
                            {selectedContactIds.length === contacts.length && contacts.length > 0
                              ? "Deselect All Contacts"
                              : `Select All Contacts (${contacts.length})`}
                          </button>
                        </div>
                      </div>

                      {/* SECTION 1: GROUPS SELECTION */}
                      <div className="space-y-2.5">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black uppercase tracking-wider text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded-md">
                              Step 1
                            </span>
                            <span className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                              Choose Group to Preview / Select All
                            </span>
                          </div>
                          {groupsWithStats.length > 2 && (
                            <input
                              type="text"
                              placeholder="Filter group names..."
                              value={groupSearch}
                              onChange={(e) => setGroupSearch(e.target.value)}
                              className="px-2.5 py-1 text-[11px] bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg w-44 outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          )}
                        </div>

                        {/* Groups Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {/* Virtual "All Contacts" Card */}
                          <div
                            onClick={() => setSelectedGroupId("all")}
                            className={`relative p-3.5 rounded-2xl border cursor-pointer transition-all duration-200 flex flex-col justify-between ${
                              selectedGroupId === "all"
                                ? "border-blue-600 bg-blue-50/80 dark:bg-blue-900/30 ring-2 ring-blue-500/30 shadow-xs"
                                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-xs"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                                  selectedGroupId === "all"
                                    ? "bg-blue-600 text-white"
                                    : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
                                }`}>
                                  <UserGroupIcon className="w-4 h-4" />
                                </div>
                                <div className="min-w-0">
                                  <div className="font-bold text-xs text-gray-900 dark:text-white truncate">
                                    All Contacts
                                  </div>
                                  <div className="text-[11px] text-gray-500">
                                    {contacts.length} total numbers
                                  </div>
                                </div>
                              </div>
                              {selectedGroupId === "all" && (
                                <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/60 px-2 py-0.5 rounded-full shrink-0">
                                  Viewing ↓
                                </span>
                              )}
                            </div>

                            <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2">
                              <span className={`text-[11px] font-semibold ${
                                allContactsStats.isAllSelected
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : allContactsStats.selectedCount > 0
                                  ? "text-blue-600 dark:text-blue-400"
                                  : "text-gray-400"
                              }`}>
                                {allContactsStats.selectedCount} / {contacts.length} selected
                              </span>
                              <button
                                type="button"
                                onClick={(e) => handleToggleGroup(allContactsStats, e)}
                                className={`text-[11px] font-bold px-2 py-0.5 rounded-lg transition-colors cursor-pointer ${
                                  allContactsStats.isAllSelected
                                    ? "text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                                    : "text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                                }`}
                              >
                                {allContactsStats.isAllSelected ? "Deselect" : "Select All"}
                              </button>
                            </div>
                          </div>

                          {/* Specific Groups Cards */}
                          {filteredGroupCards.map((grp) => {
                            const isViewing = String(selectedGroupId) === String(grp._id || grp.id);
                            return (
                              <div
                                key={grp._id || grp.id || grp.str_group_name}
                                onClick={() => setSelectedGroupId(grp._id || grp.id)}
                                className={`relative p-3.5 rounded-2xl border cursor-pointer transition-all duration-200 flex flex-col justify-between ${
                                  isViewing
                                    ? "border-blue-600 bg-blue-50/80 dark:bg-blue-900/30 ring-2 ring-blue-500/30 shadow-xs"
                                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-xs"
                                }`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                                      isViewing
                                        ? "bg-blue-600 text-white"
                                        : "bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400"
                                    }`}>
                                      <FolderIcon className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0">
                                      <div className="font-bold text-xs text-gray-900 dark:text-white truncate" title={grp.str_group_name || grp.groupName}>
                                        {grp.str_group_name || grp.groupName}
                                      </div>
                                      <div className="text-[11px] text-gray-500">
                                        {grp.totalCount} numbers
                                      </div>
                                    </div>
                                  </div>
                                  {isViewing && (
                                    <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/60 px-2 py-0.5 rounded-full shrink-0">
                                      Viewing ↓
                                    </span>
                                  )}
                                </div>

                                <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2">
                                  <span className={`text-[11px] font-semibold ${
                                    grp.isAllSelected
                                      ? "text-emerald-600 dark:text-emerald-400"
                                      : grp.selectedCount > 0
                                      ? "text-blue-600 dark:text-blue-400"
                                      : "text-gray-400"
                                  }`}>
                                    {grp.selectedCount} / {grp.totalCount} selected
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(e) => handleToggleGroup(grp, e)}
                                    className={`text-[11px] font-bold px-2 py-0.5 rounded-lg transition-colors cursor-pointer ${
                                      grp.isAllSelected
                                        ? "text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                                        : "text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                                    }`}
                                  >
                                    {grp.isAllSelected ? "Deselect" : `Select Group`}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* SECTION 2: NUMBERS IN SELECTED GROUP (BOTTOM SECTION) */}
                      <div className="space-y-3 pt-2 border-t border-gray-100 dark:border-gray-700/60">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50 dark:bg-gray-900/80 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-950/60 px-2 py-0.5 rounded-md">
                              Step 2
                            </span>
                            <div className="text-xs font-bold text-gray-900 dark:text-white">
                              Numbers in Group: <span className="text-blue-600 dark:text-blue-400 font-extrabold">{activeGroupObj.str_group_name}</span>
                              <span className="text-gray-400 font-normal ml-1.5">
                                ({filteredActiveContacts.length} numbers)
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <MagnifyingGlassIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="text"
                                placeholder={`Search numbers or names...`}
                                value={contactSearch}
                                onChange={(e) => setContactSearch(e.target.value)}
                                className="pl-8 pr-3 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl w-56 outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>

                            <button
                              type="button"
                              onClick={handleToggleAllActiveContacts}
                              className="px-3 py-1.5 text-xs font-bold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl cursor-pointer transition-colors whitespace-nowrap shadow-xs"
                            >
                              {activeGroupObj.isAllSelected ? "Deselect Group" : `Select All in ${activeGroupObj.str_group_name}`}
                            </button>
                          </div>
                        </div>

                        {/* Contacts List Grid */}
                        {filteredActiveContacts.length === 0 ? (
                          <div className="p-8 text-center bg-gray-50 dark:bg-gray-900 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 text-xs text-gray-500">
                            No contacts found in this group matching your search.
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 max-h-72 overflow-y-auto pr-1">
                            {filteredActiveContacts.map((c) => {
                              const id = c._id || c.int_contact_id || c.id;
                              const isChecked = selectedContactIds.includes(id);
                              const contactName = c.str_name || c.name || (c.str_first_name ? `${c.str_first_name} ${c.str_last_name || ""}` : "Unnamed Contact");
                              const contactPhone = c.str_phone_number || c.number || c.str_mobile_number || "";
                              const contactGroup = getContactDisplayGroup(c);

                              return (
                                <div
                                  key={id}
                                  onClick={() => handleToggleContact(id)}
                                  className={`p-3 rounded-xl border text-xs cursor-pointer transition-all flex items-center justify-between gap-3 ${
                                    isChecked
                                      ? "border-blue-600 bg-blue-50/70 dark:bg-blue-900/30 ring-1 ring-blue-500/20 shadow-xs"
                                      : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600"
                                  }`}
                                >
                                  <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => {}} // handled by container onClick
                                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer pointer-events-none shrink-0"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-bold text-gray-800 dark:text-gray-200 truncate">
                                          {contactName}
                                        </span>
                                        {contactGroup && (
                                          <span className="text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full shrink-0 truncate max-w-[120px]">
                                            {contactGroup}
                                          </span>
                                        )}
                                      </div>
                                      <div className="font-mono text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mt-0.5 truncate">
                                        <PhoneIcon className="w-3 h-3 text-emerald-500 shrink-0" />
                                        <span>{contactPhone}</span>
                                        {c.str_address && (
                                          <span className="text-[10px] text-gray-400 truncate max-w-[130px]">
                                            · {c.str_address}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {isChecked && (
                                    <div className="shrink-0 w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-xs">
                                      <CheckIcon className="w-3 h-3" />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between pt-4 border-t border-gray-100 dark:border-gray-700/60">
                    <button
                      type="button"
                      onClick={() => { directionRef.current = -1; setCurrentStep(2); }}
                      className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-xs cursor-pointer font-semibold"
                    >
                      ← Back to Title
                    </button>
                    <button
                      type="button"
                      onClick={() => handleNextStep(3)}
                      className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-xs transition-colors cursor-pointer shadow-sm"
                    >
                      Next: Variable Mapping →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 4: Variable Mapping (4th Step) ── */}
              {currentStep === 4 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 border border-gray-200 dark:border-gray-700 shadow-xs space-y-6">
                  <div>
                    <div className="flex items-center justify-between">
                      <h2 className="text-base font-bold text-gray-900 dark:text-white">
                        4. Dynamic Parameter Mapping
                      </h2>
                      {selectedTemplate?.variables && selectedTemplate.variables.length > 6 && (
                        <span className="text-[11px] font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2.5 py-0.5 rounded-full">
                          {selectedTemplate.variables.length} variables · Scroll to view all
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Map template variables ({selectedTemplate?.variables?.join(", ") || "None"}) to your audience columns or static values. The preview on the right updates instantly.
                    </p>
                  </div>

                  {(!selectedTemplate?.variables || selectedTemplate.variables.length === 0) ? (
                    <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 text-xs text-gray-500">
                      ✨ This template has no dynamic body variables. All recipients will receive the standard message.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[292px] overflow-y-auto pr-1.5">
                      {selectedTemplate.variables.map((v) => (
                        <div
                          key={v}
                          className={`p-3 bg-gray-50 dark:bg-gray-900 rounded-xl border flex flex-col justify-between shrink-0 transition-all ${variableMapping[v]?.value && String(variableMapping[v].value).trim() !== ""
                              ? "border-emerald-300 dark:border-emerald-700"
                              : "border-red-300 dark:border-red-700"
                            }`}
                          style={{ height: "88px", minHeight: "88px" }}
                        >
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-mono font-bold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/40 px-2.5 py-0.5 rounded-md text-[11px]">
                              Variable {v}
                            </span>
                            <span className="text-[10px] text-gray-400">Map to Column or Value</span>
                          </div>

                          {excelData ? (
                            <select
                              value={variableMapping[v]?.value || ""}
                              onChange={(e) => {
                                setVariableMapping({
                                  ...variableMapping,
                                  [v]: { type: "column", value: e.target.value },
                                });
                              }}
                              className="w-full px-3 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-mono cursor-pointer outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">-- Choose Excel Column --</option>
                              {excelData.columns.map((c) => (
                                <option key={c} value={c}>
                                  Column: {c}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              placeholder="Enter static fallback value..."
                              value={variableMapping[v]?.value || ""}
                              onChange={(e) => {
                                setVariableMapping({
                                  ...variableMapping,
                                  [v]: { type: "static", value: e.target.value },
                                });
                              }}
                              className="w-full px-3 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Warning banner if any variable is still unmapped */}
                  {selectedTemplate?.variables && selectedTemplate.variables.length > 0 && !allVariablesMapped && (
                    <div className="flex items-start gap-2.5 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/60 rounded-xl">
                      <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      <p className="text-[11px] text-amber-700 dark:text-amber-300 font-medium">
                        {selectedTemplate.variables.filter((v) => !(variableMapping[v]?.value && String(variableMapping[v].value).trim() !== "")).length} variable
                        {selectedTemplate.variables.filter((v) => !(variableMapping[v]?.value && String(variableMapping[v].value).trim() !== "")).length !== 1 ? "s" : ""} still need
                        {selectedTemplate.variables.filter((v) => !(variableMapping[v]?.value && String(variableMapping[v].value).trim() !== "")).length !== 1 ? "" : "s"} a value. Fill all variables (highlighted in red) before continuing.
                      </p>
                    </div>
                  )}

                  {/* Header Media Asset (Upload from Computer or Direct URL) */}
                  {selectedTemplate?.headerType && ["IMAGE", "VIDEO", "DOCUMENT"].includes(selectedTemplate.headerType) && (
                    <div className="p-5 bg-gray-50 dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="p-1.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-lg">
                              {selectedTemplate.headerType === "DOCUMENT" && <DocumentTextIcon className="w-4 h-4" />}
                              {selectedTemplate.headerType === "IMAGE" && <PhotoIcon className="w-4 h-4" />}
                              {selectedTemplate.headerType === "VIDEO" && <VideoCameraIcon className="w-4 h-4" />}
                            </span>
                            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                              Header Media Asset ({selectedTemplate.headerType})
                            </h3>
                          </div>
                          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            {selectedTemplate.headerType === "DOCUMENT" && "Upload a PDF document or catalog from your computer, or provide a web download URL."}
                            {selectedTemplate.headerType === "IMAGE" && "Upload a header image banner from your computer, or enter an image URL."}
                            {selectedTemplate.headerType === "VIDEO" && "Upload an MP4 promo video from your computer, or enter a video URL."}
                          </p>
                        </div>

                        {/* Mode toggle pills */}
                        <div className="flex bg-white dark:bg-gray-800 p-1 rounded-xl border border-gray-200 dark:border-gray-700 shrink-0">
                          <button
                            type="button"
                            onClick={() => setMediaUploadMode("computer")}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${mediaUploadMode === "computer"
                                ? "bg-blue-600 text-white shadow-xs"
                                : "text-gray-600 dark:text-gray-400 hover:text-gray-900"
                              }`}
                          >
                            📁 From Computer
                          </button>
                          <button
                            type="button"
                            onClick={() => setMediaUploadMode("url")}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${mediaUploadMode === "url"
                                ? "bg-blue-600 text-white shadow-xs"
                                : "text-gray-600 dark:text-gray-400 hover:text-gray-900"
                              }`}
                          >
                            🔗 Media URL
                          </button>
                        </div>
                      </div>

                      {/* Option A: Upload from Computer */}
                      {mediaUploadMode === "computer" && (
                        <div>
                          {!mediaUrl && !uploadedMediaName ? (
                            <label className="border-2 border-dashed border-gray-300 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 rounded-2xl p-6 text-center bg-white dark:bg-gray-800/80 transition-all cursor-pointer block group">
                              <input
                                type="file"
                                accept={
                                  selectedTemplate.headerType === "DOCUMENT"
                                    ? ".pdf,application/pdf"
                                    : selectedTemplate.headerType === "VIDEO"
                                      ? "video/mp4,video/3gpp"
                                      : "image/jpeg,image/png,image/webp,image/jpg"
                                }
                                onChange={handleMediaFileUpload}
                                className="hidden"
                              />
                              <ArrowUpTrayIcon className="w-8 h-8 mx-auto text-gray-400 group-hover:text-blue-600 transition-colors mb-2" />
                              <div className="text-xs font-bold text-gray-800 dark:text-gray-200">
                                Click to browse or drop {selectedTemplate.headerType.toLowerCase()} file from your computer
                              </div>
                              <div className="text-[11px] text-gray-400 mt-1">
                                {selectedTemplate.headerType === "DOCUMENT" && "PDF files supported (up to 25 MB)"}
                                {selectedTemplate.headerType === "IMAGE" && "PNG, JPG, JPEG, WEBP supported (up to 10 MB)"}
                                {selectedTemplate.headerType === "VIDEO" && "MP4, 3GP videos supported (up to 25 MB)"}
                              </div>
                              <span className="inline-block mt-3 px-4 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-semibold rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-all">
                                Browse Computer Files
                              </span>
                            </label>
                          ) : (
                            <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0 font-bold text-xs">
                                  {selectedTemplate.headerType === "DOCUMENT" ? "PDF" : selectedTemplate.headerType === "VIDEO" ? "VID" : "IMG"}
                                </div>
                                <div className="min-w-0">
                                  <div className="font-bold text-xs text-gray-900 dark:text-white truncate">
                                    {uploadedMediaName || "Uploaded Media File"}
                                  </div>
                                  <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-0.5">
                                    {uploadedMediaSize && <span>{uploadedMediaSize}</span>}
                                    {isUploadingMedia ? (
                                      <span className="text-amber-500 font-semibold animate-pulse">Uploading to server...</span>
                                    ) : (
                                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold">✓ Ready for Preview & Dispatch</span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 shrink-0">
                                <label className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-semibold cursor-pointer transition-colors">
                                  <input
                                    type="file"
                                    accept={
                                      selectedTemplate.headerType === "DOCUMENT"
                                        ? ".pdf,application/pdf"
                                        : selectedTemplate.headerType === "VIDEO"
                                          ? "video/mp4,video/3gpp"
                                          : "image/jpeg,image/png,image/webp,image/jpg"
                                    }
                                    onChange={handleMediaFileUpload}
                                    className="hidden"
                                  />
                                  Replace
                                </label>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMediaUrl("");
                                    setUploadedMediaName("");
                                    setUploadedMediaSize("");
                                    setMediaFile(null);
                                  }}
                                  className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors cursor-pointer"
                                  title="Remove file"
                                >
                                  <XCircleIcon className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Option B: Direct URL */}
                      {mediaUploadMode === "url" && (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <input
                              type="url"
                              placeholder={
                                selectedTemplate.headerType === "DOCUMENT"
                                  ? "https://example.com/document.pdf"
                                  : selectedTemplate.headerType === "VIDEO"
                                    ? "https://example.com/video.mp4"
                                    : "https://example.com/banner.jpg"
                              }
                              value={mediaUrl}
                              onChange={(e) => {
                                setMediaUrl(e.target.value);
                                setUploadedMediaName(e.target.value.split("/").pop().split("?")[0] || "");
                              }}
                              className="flex-1 px-3.5 py-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl font-mono text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {mediaUrl && (
                              <button
                                type="button"
                                onClick={() => {
                                  setMediaUrl("");
                                  setUploadedMediaName("");
                                  setUploadedMediaSize("");
                                }}
                                className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl text-xs hover:bg-gray-200 transition-colors cursor-pointer"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                          <p className="text-[10px] text-gray-400">
                            Public HTTPS URL directly accessible by Meta WhatsApp API servers.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between pt-4 border-t border-gray-100 dark:border-gray-700/60">
                    <button
                      type="button"
                      onClick={() => { directionRef.current = -1; setCurrentStep(3); }}
                      className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-xs cursor-pointer font-semibold"
                    >
                      ← Back to Audience
                    </button>
                    <button
                      type="button"
                      disabled={!allVariablesMapped}
                      onClick={() => handleNextStep(4)}
                      title={!allVariablesMapped ? "Please map all template variables before proceeding." : ""}
                      className={`px-6 py-2.5 font-semibold rounded-xl text-xs transition-colors shadow-sm ${allVariablesMapped
                          ? "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        }`}
                    >
                      Next: Sender & Launch →
                    </button>
                  </div>
                </div>
              )}

              {/* ── STEP 5: Sender WhatsApp Number, Schedule & Broadcast Launch (Last Step) ── */}
              {currentStep === 5 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 border border-gray-200 dark:border-gray-700 shadow-xs space-y-6">
                  <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white">
                      5. Sender Channel, Schedule & Broadcast Launch
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Select your WhatsApp sender number, choose the dispatch schedule, review your configuration, and launch the broadcast.
                    </p>
                  </div>

                  {/* Sender WhatsApp Number and Schedule Configuration */}
                  <div className="p-5 bg-gray-50 dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 space-y-5">
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="block text-xs font-bold text-gray-700 dark:text-gray-300">
                          Sender Selection <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label
                            onClick={() => setSenderSelectionMode("single")}
                            className={`p-3 rounded-xl border flex items-start gap-3 cursor-pointer transition-colors ${
                              senderSelectionMode === "single"
                                ? "border-blue-500 bg-blue-50/40 dark:bg-blue-950/20"
                                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
                            }`}
                          >
                            <input
                              type="radio"
                              name="senderSelectionMode"
                              checked={senderSelectionMode === "single"}
                              onChange={() => setSenderSelectionMode("single")}
                              className="mt-0.5 text-blue-600"
                            />
                            <div>
                              <div className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                                <PhoneIcon className="w-3.5 h-3.5 text-blue-600" />
                                Single Number
                              </div>
                              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                All eligible campaign messages dispatch through one designated verified number.
                              </p>
                            </div>
                          </label>

                          <label
                            onClick={() => setSenderSelectionMode("multiple")}
                            className={`p-3 rounded-xl border flex items-start gap-3 cursor-pointer transition-colors ${
                              senderSelectionMode === "multiple"
                                ? "border-blue-500 bg-blue-50/40 dark:bg-blue-950/20"
                                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
                            }`}
                          >
                            <input
                              type="radio"
                              name="senderSelectionMode"
                              checked={senderSelectionMode === "multiple"}
                              onChange={() => setSenderSelectionMode("multiple")}
                              className="mt-0.5 text-blue-600"
                            />
                            <div>
                              <div className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                                <UserGroupIcon className="w-3.5 h-3.5 text-indigo-600" />
                                Multiple Numbers
                              </div>
                              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                Distribute recipient messages across multiple verified numbers via Same or Different distribution.
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Single Number Configuration */}
                      {senderSelectionMode === "single" && (
                        <div className="space-y-1.5 pt-2">
                          <label className="block text-xs font-bold text-gray-700 dark:text-gray-300">
                            Sender WhatsApp Number <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={senderNumber}
                            onChange={(e) => setSenderNumber(e.target.value)}
                            className="w-full px-4 py-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white font-mono cursor-pointer outline-none"
                          >
                            {phoneNumbers.map((p) => (
                              <option key={p._id || p.int_phone_number_id} value={p.str_phone_number}>
                                {p.str_phone_number} {p.str_verified_name ? `(${p.str_verified_name})` : ""}
                              </option>
                            ))}
                          </select>
                          <p className="text-[11px] text-gray-400">
                            The verified WhatsApp Business Account number through which messages will be delivered.
                          </p>
                        </div>
                      )}

                      {/* Multiple Numbers Configuration */}
                      {senderSelectionMode === "multiple" && (
                        <div className="space-y-4 pt-2">
                          {/* Senders Picker */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <label className="text-xs font-bold text-gray-700 dark:text-gray-300">
                                Select WhatsApp Sender Pool ({selectedSenderNumbers.length}/{phoneNumbers.length} Selected)
                              </label>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setSelectedSenderNumbers(phoneNumbers.map((p) => p.str_phone_number).filter(Boolean))}
                                  className="text-[11px] font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 cursor-pointer"
                                >
                                  Select All
                                </button>
                                <span className="text-gray-300 dark:text-gray-600">|</span>
                                <button
                                  type="button"
                                  onClick={() => setSelectedSenderNumbers([])}
                                  className="text-[11px] font-bold text-gray-500 hover:text-gray-700 dark:text-gray-400 cursor-pointer"
                                >
                                  Deselect All
                                </button>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 max-h-48 overflow-y-auto p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                              {phoneNumbers.length === 0 ? (
                                <p className="text-xs text-gray-400 col-span-3 py-3 text-center">No sender numbers configured.</p>
                              ) : (
                                phoneNumbers.map((p) => {
                                  const isChecked = selectedSenderNumbers.includes(p.str_phone_number);
                                  return (
                                    <label
                                      key={p._id || p.int_phone_number_id || p.str_phone_number}
                                      className={`flex items-center gap-2.5 p-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                                        isChecked
                                          ? "border-blue-400 bg-blue-50/50 dark:bg-blue-950/30 text-gray-900 dark:text-white"
                                          : "border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 hover:border-gray-300"
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedSenderNumbers((prev) => [...prev, p.str_phone_number]);
                                          } else {
                                            setSelectedSenderNumbers((prev) => prev.filter((n) => n !== p.str_phone_number));
                                          }
                                        }}
                                        className="rounded text-blue-600"
                                      />
                                      <div className="min-w-0 flex-1">
                                        <div className="font-mono font-semibold text-xs truncate">{p.str_phone_number}</div>
                                        <div className="text-[10px] text-gray-400 truncate">{p.str_verified_name || "WhatsApp Business"}</div>
                                      </div>
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          </div>

                          {/* Sender Distribution Mode */}
                          <div className="space-y-2">
                            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300">
                              Sender Distribution <span className="text-red-500">*</span>
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <label
                                onClick={() => setSenderDistributionMode("SAME")}
                                className={`p-3 rounded-xl border flex items-start gap-3 cursor-pointer transition-colors ${
                                  senderDistributionMode === "SAME"
                                    ? "border-blue-500 bg-blue-50/40 dark:bg-blue-950/20"
                                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="senderDistributionMode"
                                  checked={senderDistributionMode === "SAME"}
                                  onChange={() => setSenderDistributionMode("SAME")}
                                  className="mt-0.5 text-blue-600"
                                />
                                <div>
                                  <div className="text-xs font-bold text-gray-900 dark:text-white">
                                    Same Distribution
                                  </div>
                                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    Recipients divided as evenly as possible across numbers with fair remainder allocation.
                                  </p>
                                </div>
                              </label>

                              <label
                                onClick={() => setSenderDistributionMode("DIFFERENT")}
                                className={`p-3 rounded-xl border flex items-start gap-3 cursor-pointer transition-colors ${
                                  senderDistributionMode === "DIFFERENT"
                                    ? "border-blue-500 bg-blue-50/40 dark:bg-blue-950/20"
                                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="senderDistributionMode"
                                  checked={senderDistributionMode === "DIFFERENT"}
                                  onChange={() => setSenderDistributionMode("DIFFERENT")}
                                  className="mt-0.5 text-blue-600"
                                />
                                <div>
                                  <div className="text-xs font-bold text-gray-900 dark:text-white flex items-center justify-between">
                                    <span>Different Distribution</span>
                                  </div>
                                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    Dynamically generated, controlled randomized allocation varying from batch to batch.
                                  </p>
                                </div>
                              </label>
                            </div>
                          </div>

                          {/* Live Distribution Preview Breakdown */}
                          {selectedSenderNumbers.length > 0 && totalRecipientsCount > 0 && (
                            <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 space-y-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                                    <span>📊 Live Distribution Preview</span>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300">
                                      {senderDistributionMode === "SAME" ? "Even Partition" : "Controlled Randomized Partition"}
                                    </span>
                                  </h4>
                                  <p className="text-[11px] text-gray-400 mt-0.5">
                                    Audience breakdown across {selectedSenderNumbers.length} selected senders ({totalRecipientsCount} recipients)
                                  </p>
                                </div>
                                {senderDistributionMode === "DIFFERENT" && (
                                  <button
                                    type="button"
                                    onClick={() => setDistributionSeed((prev) => prev + 1)}
                                    className="px-2.5 py-1 text-[11px] font-bold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/60 dark:text-blue-400 dark:hover:bg-blue-900/60 rounded-lg flex items-center gap-1 cursor-pointer transition-colors"
                                  >
                                    <ArrowPathIcon className="w-3 h-3" />
                                    Re-shuffle 🎲
                                  </button>
                                )}
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                {liveDistributionPreview.map((item, idx) => (
                                  <div
                                    key={idx}
                                    className="p-2.5 rounded-lg border border-gray-100 dark:border-gray-700/60 bg-gray-50/70 dark:bg-gray-900/40 space-y-1.5"
                                  >
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="font-mono font-bold text-gray-800 dark:text-gray-200 truncate">{item.number}</span>
                                      <span className="font-bold text-blue-600 dark:text-blue-400">{item.count} msgs</span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden">
                                      <div
                                        className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                        style={{ width: item.percent }}
                                      />
                                    </div>
                                    <div className="flex justify-between text-[10px] text-gray-400">
                                      <span>Sender {idx + 1}</span>
                                      <span>{item.percent}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 flex items-center justify-between text-[11px]">
                                <span className="text-gray-500 dark:text-gray-400">Total Assigned Sum:</span>
                                <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                                  {liveDistributionPreview.reduce((acc, curr) => acc + curr.count, 0)} / {totalRecipientsCount} recipients (100% matched)
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Schedule & Delivery Window */}
                      <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700/60">
                        <label className="block text-xs font-bold text-gray-700 dark:text-gray-300">
                          Schedule & Delivery Window <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <label className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs cursor-pointer flex-1">
                            <input
                              type="radio"
                              name="schedule"
                              checked={scheduleType === "now"}
                              onChange={() => setScheduleType("now")}
                              className="text-blue-600"
                            />
                            <span className="font-semibold text-gray-800 dark:text-gray-200">
                              Send Immediately
                            </span>
                          </label>
                          <label className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs cursor-pointer flex-1">
                            <input
                              type="radio"
                              name="schedule"
                              checked={scheduleType === "later"}
                              onChange={() => setScheduleType("later")}
                              className="text-blue-600"
                            />
                            <span className="font-semibold text-gray-800 dark:text-gray-200">
                              Schedule for Later
                            </span>
                          </label>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                          <label className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs cursor-pointer flex-1">
                            <div>
                              <span className="text-gray-400 uppercase font-semibold text-[10px]">Sender Channel</span>
                              <div className="font-bold text-gray-900 dark:text-white font-mono text-sm mt-0.5">
                                {senderSelectionMode === "multiple"
                                  ? `${selectedSenderNumbers.length} Senders (${senderDistributionMode === "DIFFERENT" ? "Different" : "Same"} Dist)`
                                  : (senderNumber || "Not Selected")}
                              </div>
                            </div>
                          </label>
                          <label className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs cursor-pointer flex-1">
                            <div>
                              <span className="text-gray-400 uppercase font-semibold text-[10px]">Total Audience</span>
                              <div className="font-bold text-emerald-600 dark:text-emerald-400 text-sm mt-0.5">
                                {totalRecipientsCount} Recipients
                              </div>
                            </div>
                          </label>
                        </div>

                        {scheduleType === "later" && (
                          <div className="pt-2">
                            <input
                              type="datetime-local"
                              value={scheduledAt}
                              onChange={(e) => setScheduledAt(e.target.value)}
                              className="w-full px-3 py-2 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Country Rates, Category Pricing & Balance Verification Card */}
                  <div className="bg-white dark:bg-gray-900/90 rounded-2xl p-5 border border-blue-200 dark:border-blue-900/50 shadow-xs space-y-4">

                    {/* Insufficient Balance Banner */}
                    {costData && costData.hasSufficientBalance === false && (
                      <div className="p-3.5 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 rounded-xl flex items-start gap-2.5 text-xs text-red-800 dark:text-red-300">
                        <span className="text-base shrink-0">⚠️</span>
                        <div className="space-y-0.5">
                          <div className="font-bold">Insufficient Wallet Balance</div>
                          <p className="text-[11px] opacity-90">
                            This broadcast requires <strong>₹{Number(costData.totalEstimatedCost).toFixed(2)}</strong>, but your wallet currently has <strong>₹{Number(costData.currentBalance).toFixed(2)}</strong>. Please top up your wallet credits before launching.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Country Breakdown Table */}
                    {costData?.countryBreakdown && costData.countryBreakdown.length > 0 && (
                      <div className="space-y-2 pt-1">
                        <div className="text-[11px] font-bold text-gray-700 dark:text-gray-300 flex items-center justify-between">
                          <span>Destination Countries & Custom Deduct Rates</span>
                          <span className="text-gray-400 text-[10px] font-normal">
                            {costData.countryBreakdown.length} destination country(ies)
                          </span>
                        </div>

                        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 uppercase text-[10px] font-bold">
                              <tr>
                                <th className="px-3.5 py-2">Destination Country</th>
                                <th className="px-3.5 py-2">Code</th>
                                <th className="px-3.5 py-2">Category</th>
                                <th className="px-3.5 py-2">Audience</th>
                                <th className="px-3.5 py-2 text-right">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-300 text-[11px]">
                              {costData.countryBreakdown.map((c, idx) => (
                                <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/40">
                                  <td className="px-3.5 py-2 font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                                    <span className="font-bold text-blue-600">{c.countryCode}</span>
                                    <span>{c.countryName}</span>
                                  </td>
                                  <td className="px-3.5 py-2 font-mono text-gray-600 dark:text-gray-400">
                                    {c.callingCode}
                                  </td>
                                  <td className="px-3.5 py-2 uppercase font-medium text-gray-600 dark:text-gray-400">
                                    {c.category}
                                  </td>
                                  <td className="px-3.5 py-2 font-bold">
                                    {c.count} msgs
                                  </td>
                                  <td className="px-3.5 py-2 text-right">
                                    {c.isAllowed ? (
                                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                        Enabled
                                      </span>
                                    ) : (
                                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700 dark:bg-red-950/60 dark:text-red-400 border border-red-200 dark:border-red-800">
                                        Disabled
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Disallowed Destination Alert */}
                    {costData?.disallowedRecipients && costData.disallowedRecipients.length > 0 && (
                      <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 rounded-xl text-xs text-amber-800 dark:text-amber-300">
                        <span className="font-bold">⚠️ International Destination Notice:</span> {costData.disallowedRecipients.length} numbers belong to countries not enabled for your account. Please enable international permissions in <em>Countries & Rates</em> or these recipients will be skipped.
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-100 dark:border-gray-700/60">
                    <button
                      type="button"
                      onClick={() => { directionRef.current = -1; setCurrentStep(4); }}
                      className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-xs cursor-pointer font-semibold"
                    >
                      ← Back to Mapping
                    </button>

                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => handleSaveOrSend(false)}
                        className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-bold rounded-xl text-xs transition-colors cursor-pointer"
                      >
                        {isEditing ? "Update Draft" : "Save as Draft"}
                      </button>

                      <button
                        type="button"
                        disabled={isSubmitting || (costData && costData.hasSufficientBalance === false)}
                        onClick={() => handleSaveOrSend(true)}
                        className={`px-6 py-2.5 font-bold rounded-xl text-xs transition-colors shadow-md flex items-center gap-2 ${costData && costData.hasSufficientBalance === false
                            ? "bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed shadow-none"
                            : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer shadow-blue-500/20"
                          }`}
                        title={costData && costData.hasSufficientBalance === false ? "Insufficient wallet balance to launch broadcast" : "Launch Broadcast Now"}
                      >
                        <PaperAirplaneIcon className="w-4 h-4" />
                        Launch Broadcast Now 🚀
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── RIGHT COLUMN: FIXED / STICKY WHATSAPP LIVE PREVIEW (VISIBLE ACROSS ALL STEPS) ── */}
        <div className="lg:col-span-5 xl:col-span-4">
          <div className="sticky top-6 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <span>📱 WhatsApp Live Preview</span>
              </h3>
              {selectedTemplate && (
                <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800/60">
                  {selectedTemplate.category}
                </span>
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col items-center justify-center">
              <WhatsAppPreview
                templateName={selectedTemplate?.name || "Template Preview"}
                headerType={selectedTemplate?.headerType || "NONE"}
                headerText={selectedTemplate?.headerText || ""}
                headerPreview={mediaUrl || null}
                mediaFileName={uploadedMediaName || (mediaUrl ? mediaUrl.split("/").pop().split("?")[0] : "")}
                mediaFileSize={uploadedMediaSize}
                bodyText={
                  selectedTemplate?.bodyText ||
                  "Please select an approved WhatsApp template from the list on the left to preview how it appears to customers."
                }
                footerText={selectedTemplate?.footerText || ""}
                buttons={selectedTemplate?.buttons || []}
                samples={previewSamples}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
